// chat-steps.ts — WS5 pure reducer for the chat reasoning-steps strip.
//
// The worker (worker/src/lib/steps.js, gated behind STREAM_STEPS) emits
// `{type:'step', id, label, status:'running'|'done', detail?}` SSE events.
// streamChat (lib/api.ts) forwards each to `onStep`. This module holds the
// pure state transition so it is trivially unit-testable in isolation and the
// ChatPage handler stays a thin wrapper.
//
// Design (locked by Eric):
//   • Steps render as a vertical list keyed by `id`.
//   • A new `running` step marks the PRIOR (still-running) step `done` — the
//     worker emits stages sequentially, so a fresh stage starting implies the
//     one before it finished even if its explicit `done` hasn't landed yet.
//   • A `done` event for an id marks that id done.
//   • First token (onContent) → mark ALL steps done (finalizeSteps).
//   • Min-visible ~350ms: a step carries a `startedAt` wall-clock stamp; the
//     STRIP (not this reducer) uses it to hold a just-appended step visible for
//     ~350ms so a sub-300ms stage doesn't flash by. Keeping the timing in the
//     view layer means this reducer stays pure + synchronous (easy to test).

import type { ChatStep } from "@/lib/api";

// A step as tracked in ChatPage state: the wire `ChatStep` plus a client-only
// `startedAt` used purely for the min-visible-duration hold in the strip.
export type UIStep = ChatStep & { startedAt: number };

// Minimum time (ms) a step stays visible before the strip lets it be visually
// superseded/removed, so a sub-300ms stage doesn't flash by. Consumed by the
// strip's render logic, exported here so the reducer + strip agree on one value.
export const MIN_VISIBLE_MS = 350;

/**
 * Apply one step event to the current list.
 *
 *  - Unknown/duplicate `running` for an existing id → refresh its label/detail,
 *    keep its original startedAt (don't reset the min-visible clock).
 *  - New `running` id → mark any prior still-running step done, append the new
 *    step stamped with `now`.
 *  - `done` for an id → mark that id done (append if somehow unseen).
 *
 * `now` is injected so tests are deterministic; defaults to Date.now().
 */
export function applyStep(
  steps: UIStep[],
  event: ChatStep,
  now: number = Date.now(),
): UIStep[] {
  const existingIdx = steps.findIndex((s) => s.id === event.id);

  if (event.status === "running") {
    if (existingIdx >= 0) {
      // Re-emit for a known id: update label/detail/status, keep startedAt.
      return steps.map((s, i) =>
        i === existingIdx
          ? { ...s, label: event.label, detail: event.detail, status: "running" }
          : s,
      );
    }
    // New stage started → the previous still-running stage is implicitly done.
    const withPriorDone = steps.map((s) =>
      s.status === "running" ? { ...s, status: "done" as const } : s,
    );
    return [
      ...withPriorDone,
      {
        type: "step",
        id: event.id,
        label: event.label,
        status: "running",
        detail: event.detail,
        startedAt: now,
      },
    ];
  }

  // status === "done"
  if (existingIdx >= 0) {
    return steps.map((s, i) =>
      i === existingIdx
        ? { ...s, label: event.label, detail: event.detail ?? s.detail, status: "done" }
        : s,
    );
  }
  // A done for a never-seen id (defensive) — record it already-complete.
  return [
    ...steps,
    {
      type: "step",
      id: event.id,
      label: event.label,
      status: "done",
      detail: event.detail,
      startedAt: now,
    },
  ];
}

/** Mark every step done — called on the first streamed token (onContent). */
export function finalizeSteps(steps: UIStep[]): UIStep[] {
  if (steps.length === 0) return steps;
  if (steps.every((s) => s.status === "done")) return steps;
  return steps.map((s) => (s.status === "running" ? { ...s, status: "done" as const } : s));
}
